/**
 * Generacion del nivel: determinista a partir del seed que manda el servidor.
 *
 * OJO. Este fichero tiene un gemelo en `backend/app/services/nivel.py`. El
 * servidor genera el mismo nivel para recalcular cuantas monedas eran posibles
 * en la partida; si los dos se desvian, el antifraude acusa de trampa a
 * jugadores honestos. Los numeros no estan aqui: estan en
 * `shared/nivel-config.json`, que leen las dos caras.
 *
 * Si tocas esto, toca el gemelo y corre `backend/tests/test_nivel_paridad.py`.
 *
 * Decision de diseno importante: cada segmento se genera a partir del seed Y de
 * su indice, SIN depender del segmento anterior. Asi el servidor puede calcular
 * el segmento 400 sin recorrer los 399 de antes, y el cliente puede generar el
 * nivel por delante del jugador sin guardar nada.
 */

import cfg from '@compartido/nivel-config.json';
import { mulberry32, type Rng } from './prng';

export type Carril = -1 | 0 | 1;
export type TipoObstaculo = 'barrera' | 'vela' | 'tiburon';
export type TipoRecolectable = 'moneda' | 'iman' | 'x2' | 'escudo' | 'cohete' | 'ballena';
export type Bioma = 'ciudad' | 'estratosfera' | 'espacio';

export interface Obstaculo {
  tipo: TipoObstaculo;
  carril: Carril;
  z: number;
}

export interface Recolectable {
  tipo: TipoRecolectable;
  carril: Carril;
  z: number;
  /** Monedas que da. 1 para una moneda, el bonus para la ballena, 0 para los potenciadores. */
  valor: number;
}

export interface Segmento {
  indice: number;
  z0: number;
  bioma: Bioma;
  obstaculos: Obstaculo[];
  recolectables: Recolectable[];
}

export const LARGO_SEGMENTO = cfg.segmento.largo;
export const ANCHO_CARRIL = cfg.anchoCarril;
/** Metros que dura cada bioma antes de cambiar al siguiente. */
export const LARGO_BIOMA = cfg.biomas.largo;
export const CARRILES: readonly Carril[] = [-1, 0, 1];

/** Cuantas ranuras de profundidad tiene un segmento. */
const RANURAS = 3;

/**
 * Numero de oro de 32 bits. Mezcla el indice del segmento con el seed para que
 * segmentos consecutivos no den secuencias parecidas.
 * `Math.imul` en vez de `*` para que sea exactamente 32 bits, igual que en Python.
 */
const MEZCLA = 0x9e3779b9;

function rngDeSegmento(seed: number, indice: number): Rng {
  return mulberry32((seed ^ Math.imul(indice, MEZCLA)) >>> 0);
}

/** El bioma de un segmento. Cambia cada `biomas.largo` metros y luego se repite. */
export function biomaDeSegmento(indice: number): Bioma {
  const metros = indice * LARGO_SEGMENTO;
  const i = Math.floor(metros / cfg.biomas.largo) % cfg.biomas.nombres.length;
  return cfg.biomas.nombres[i] as Bioma;
}

/**
 * Velocidad a una distancia dada. Curva logaritmica: sube rapido al principio,
 * donde el jugador lo nota, y se aplana al final, donde ya seria injugable.
 */
export function velocidadEn(distancia: number): number {
  const v = cfg.velocidad;
  return Math.min(v.maxima, v.inicial + v.curvaK * Math.log(1 + distancia / 120));
}

/**
 * Eleccion con pesos. El orden de `orden` manda: hay que recorrer las claves en
 * el mismo orden en las dos implementaciones o el resultado se separa.
 */
function elegirConPesos(
  rng: Rng,
  pesos: Readonly<Record<string, number>>,
  orden: readonly string[]
): string {
  let total = 0;
  for (const k of orden) total += pesos[k] ?? 0;
  let r = rng() * total;
  for (const k of orden) {
    r -= pesos[k] ?? 0;
    if (r < 0) return k;
  }
  // Solo se llega aqui por redondeo cuando rng() devuelve casi 1.
  return orden[orden.length - 1] as string;
}

/** Entero en [lo, hi] usando el rng dado. Duplicado a proposito para no importar de prng. */
function entero(rng: Rng, lo: number, hi: number): number {
  return lo + Math.floor(rng() * (hi - lo + 1));
}

/**
 * Genera un segmento completo.
 *
 * INVARIANTE que este codigo garantiza: en una misma ranura de profundidad
 * nunca se ocupan los tres carriles. Si se ocuparan, el nivel seria imposible
 * de pasar, porque el tiburon FUD no se puede saltar ni esquivar por debajo:
 * hay que cambiarse de carril. Se comprueba en los tests de las dos caras.
 */
export function generarSegmento(seed: number, indice: number): Segmento {
  const rng = rngDeSegmento(seed, indice);
  const z0 = indice * LARGO_SEGMENTO;
  const bioma = biomaDeSegmento(indice);
  const largoRanura = LARGO_SEGMENTO / RANURAS;

  const obstaculos: Obstaculo[] = [];
  const recolectables: Recolectable[] = [];

  // --- Obstaculos ---
  const pesos = cfg.obstaculos.pesosPorBioma[bioma] as Record<string, number>;
  const nObst = entero(rng, cfg.obstaculos.porSegmentoMin, cfg.obstaculos.porSegmentoMax);

  // Qué ranura y carriles ocupa cada obstaculo. Se guarda para que las monedas
  // no aparezcan dentro de un obstaculo.
  const ocupado: Array<Set<number>> = [new Set(), new Set(), new Set()];

  // En los dos bucles de abajo se piden TODOS los numeros aleatorios antes de
  // decidir si la pieza se coloca. Es a proposito: si el descarte ocurriera
  // antes de pedirlos, el gemelo en Python tendria que saltarse las mismas
  // llamadas en el mismo sitio exacto para no desalinearse. Asi el numero de
  // llamadas al rng solo depende de los contadores, no de las decisiones, y las
  // dos implementaciones son mucho mas dificiles de separar por accidente.
  // Si cada ranura ya tiene su tiburon. Ver la regla de abajo.
  const tiburonEnRanura = [false, false, false];

  for (let k = 0; k < nObst; k++) {
    const ranura = entero(rng, 0, RANURAS - 1);
    const idxCarril = Math.floor(rng() * 3);
    let tipo = elegirConPesos(rng, pesos, cfg.obstaculos.tipos) as TipoObstaculo;
    // Un poco de dispersion dentro de la ranura, para que no quede en rejilla.
    const z = z0 + (ranura + 0.5) * largoRanura + (rng() - 0.5) * largoRanura * 0.4;

    const ocupadosAqui = ocupado[ranura] as Set<number>;
    const carril = CARRILES[idxCarril] as Carril;
    // INVARIANTE 1: como maximo 2 carriles por ranura, siempre queda uno libre.
    // Si esta ranura ya tiene dos, o ese carril ya esta cogido, se descarta.
    if (ocupadosAqui.size >= 2 || ocupadosAqui.has(carril)) continue;

    // INVARIANTE 2: como mucho UN tiburon por ranura.
    //
    // El tiburon es el unico obstaculo que no se puede librar con una accion:
    // hay que cambiarse de carril. Dos tiburones tapando dos carriles contiguos
    // dejan libre solo el del otro extremo, y cruzar DOS carriles no siempre
    // cabe: a 34 m/s un cambio cuesta 6,8 m, y entre dos grupos puede haber 9.
    // Se midio el 30-sep-2026: pasaba 15 veces en 7.200 segmentos, o sea casi
    // nunca, que es justo lo peor: arruina la partida buena y nadie entiende
    // por que. Con un solo tiburon, el otro carril ocupado siempre lleva una
    // barrera o una vela, que se libran saltando o agachandose sin moverse.
    if (tipo === 'tiburon' && tiburonEnRanura[ranura]) tipo = 'barrera';
    if (tipo === 'tiburon') tiburonEnRanura[ranura] = true;

    ocupadosAqui.add(carril);
    obstaculos.push({ tipo, carril, z });
  }

  // Pista de despegue: los primeros segmentos se quedan sin obstaculos.
  //
  // Se generan y LUEGO se tiran, en vez de no generarlos. Parece un desperdicio
  // y es justo lo contrario: asi el numero de llamadas al rng es el mismo haya o
  // no arranque seguro, y el gemelo en Python no puede desalinearse por este
  // motivo. Ademas `ocupado` sigue valiendo para que las monedas no caigan donde
  // habria habido un obstaculo, con lo que el reparto de monedas no cambia.
  if (indice < cfg.obstaculos.arranqueSeguroSegmentos) {
    obstaculos.length = 0;
  }

  // --- Monedas ---
  const nMonedas = entero(
    rng,
    cfg.recolectables.monedasPorSegmentoMin,
    cfg.recolectables.monedasPorSegmentoMax
  );
  for (let k = 0; k < nMonedas; k++) {
    const ranura = entero(rng, 0, RANURAS - 1);
    const carril = CARRILES[Math.floor(rng() * 3)] as Carril;
    const z = z0 + (ranura + 0.5) * largoRanura + (rng() - 0.5) * largoRanura * 0.7;
    // Una moneda dentro de un obstaculo seria una moneda imposible de coger, y
    // el servidor la contaria en el maximo teorico. Se descarta.
    if ((ocupado[ranura] as Set<number>).has(carril)) continue;
    recolectables.push({ tipo: 'moneda', carril, z, valor: 1 });
  }

  // --- Potenciador (como mucho uno por segmento) ---
  if (rng() < cfg.recolectables.probPotenciador) {
    const tipo = elegirConPesos(
      rng,
      cfg.recolectables.pesosPotenciador,
      cfg.recolectables.potenciadores
    ) as TipoRecolectable;
    const carril = CARRILES[Math.floor(rng() * 3)] as Carril;
    const z = z0 + LARGO_SEGMENTO * (0.2 + rng() * 0.6);
    recolectables.push({ tipo, carril, z, valor: 0 });
  }

  // --- Ballena (el huevo de pascua) ---
  if (
    indice >= cfg.recolectables.ballenaDesdeSegmento &&
    rng() < cfg.recolectables.probBallena
  ) {
    const carril = CARRILES[Math.floor(rng() * 3)] as Carril;
    const z = z0 + LARGO_SEGMENTO * 0.5;
    recolectables.push({
      tipo: 'ballena',
      carril,
      z,
      valor: cfg.recolectables.ballenaBonus,
    });
  }

  return { indice, z0, bioma, obstaculos, recolectables };
}

/**
 * Cota superior de monedas posibles en los primeros `nSegmentos`.
 *
 * La usa el antifraude del servidor. Es GENEROSA a proposito: suma todo lo
 * recolectable y, si en el tramo habia algun x2, lo multiplica por dos aunque
 * en la practica sea imposible pillarlo todo con el x2 activo. Dejar pasar a un
 * tramposo torpe es mucho menos grave que quitarle un premio a alguien honesto;
 * los casos de verdad los caza el replay del log de entradas, no esta cota.
 */
export function monedasMaximas(seed: number, nSegmentos: number): number {
  let base = 0;
  let hayX2 = false;
  for (let i = 0; i < nSegmentos; i++) {
    const seg = generarSegmento(seed, i);
    for (const r of seg.recolectables) {
      base += r.valor;
      if (r.tipo === 'x2') hayX2 = true;
    }
  }
  return hayX2 ? base * 2 : base;
}

/** Cuantos segmentos cubren una distancia dada (redondeando hacia arriba). */
export function segmentosParaDistancia(distancia: number): number {
  return Math.max(1, Math.ceil(distancia / LARGO_SEGMENTO));
}
